import { useState } from "react";
import HelpButton from "../components/HelpButton";
import Sidebar from "../components/Sidebar";
import { API } from "../App";
import { DownloadSimple, FileCsv, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { hasPerm } from "../utils/permissions";

const REPORT_TYPES = [
  { key: "products", label: "Products Catalog", description: "All security products with pricing and features" },
  { key: "leads", label: "Leads Report", description: "Customer leads with status and contact details" },
  { key: "contacts", label: "CRM Contacts", description: "All CRM contacts with company and tags" },
  { key: "deals", label: "CRM Deals", description: "Deal pipeline with values and stages" },
  { key: "campaigns", label: "Campaigns", description: "Marketing campaigns with status and audience" }
];

const Reports = ({ user, logout }) => {
  const [downloading, setDownloading] = useState("");
  const canExport = hasPerm(user, "reports.export");

  const downloadReport = async (reportType, format) => {
    const key = `${reportType}_${format}`;
    setDownloading(key);
    try {
      const url = `${API}/reports/${reportType}/${format}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${reportType}_report.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success(`${reportType} ${format.toUpperCase()} downloaded!`);
    } catch (error) {
      toast.error(`Failed to download ${reportType} report`);
    } finally {
      setDownloading("");
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="p-8">
          <div className="mb-8">
            <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
              Export Reports
            </h1><HelpButton moduleKey="reports" /></div>
            <p className="text-base leading-relaxed text-slate-700">
              Download reports in CSV or PDF format
            </p>
          </div>

          <div data-testid="reports-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REPORT_TYPES.map((report) => (
              <div key={report.key} data-testid={`report-card-${report.key}`}
                className="bg-white border border-slate-200 rounded-none p-6 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <DownloadSimple size={28} weight="duotone" className="text-[#002FA7]" />
                  <h3 className="text-lg font-semibold" style={{fontFamily: 'Outfit, sans-serif'}}>{report.label}</h3>
                </div>
                <p className="text-sm text-slate-600 mb-6">{report.description}</p>
                <div className="flex gap-3">
                  {canExport && (
                  <button data-testid={`export-${report.key}-csv`}
                    onClick={() => downloadReport(report.key, 'csv')}
                    disabled={downloading === `${report.key}_csv`}
                    className="flex-1 rounded-none bg-[#10B981] text-white hover:bg-[#059669] px-4 py-2.5 text-sm font-medium transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50">
                    <FileCsv size={18} weight="fill" />
                    {downloading === `${report.key}_csv` ? 'Downloading...' : 'CSV'}
                  </button>
                  )}
                  {canExport && (
                  <button data-testid={`export-${report.key}-pdf`}
                    onClick={() => downloadReport(report.key, 'pdf')}
                    disabled={downloading === `${report.key}_pdf`}
                    className="flex-1 rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2.5 text-sm font-medium transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50">
                    <FilePdf size={18} weight="fill" />
                    {downloading === `${report.key}_pdf` ? 'Downloading...' : 'PDF'}
                  </button>
                  )}
                  {!canExport && (
                    <span data-testid={`export-${report.key}-blocked`} className="text-xs text-slate-400 italic">No export permission</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
